package org.gymly.ui.admin

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.isShiftPressed
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onPreviewKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.gymly.model.AdminExchangeRateQuote
import org.gymly.model.AdminExchangeRateStatus
import org.gymly.theme.AdminColors
import org.gymly.util.GymlyCurrency
import org.gymly.util.formatMoney
import kotlin.math.roundToLong

@Composable
fun DualCurrencyAmountFields(
    usdInput: String,
    vesInput: String,
    onUsdChange: (String) -> Unit,
    onVesChange: (String) -> Unit,
    exchangeQuote: AdminExchangeRateQuote?,
    enabled: Boolean,
    modifier: Modifier = Modifier,
    /** Si false, solo USD (Divisas/USDT en cobros mixtos). Bs no se edita ni se suma a Total Bs. */
    showVesField: Boolean = false,
    /** Con el módulo de tasas apagado no se muestra el aviso de cotización. */
    exchangeRatesOn: Boolean = true,
) {
    val focusUsd = remember { FocusRequester() }
    val focusVes = remember { FocusRequester() }
    val equivBs = remember(usdInput, exchangeQuote) {
        val usdCents = parseBulkAmountToCents(usdInput)
        if (usdCents <= 0 || exchangeQuote == null) null
        else convertDivisasLocally(usdCents, exchangeQuote)?.vesCents
    }
    Column(modifier) {
        if (showVesField) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                Column(Modifier.weight(1f)) {
                    Text("Monto (USD)", color = AdminColors.TextMuted, fontSize = 12.sp)
                    Spacer(Modifier.height(4.dp))
                    OutlinedTextField(
                        value = usdInput,
                        onValueChange = onUsdChange,
                        enabled = enabled,
                        modifier = Modifier
                            .fillMaxWidth()
                            .focusRequester(focusUsd)
                            .onPreviewKeyEvent { event ->
                                if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                                when (event.key) {
                                    Key.Enter, Key.NumPadEnter, Key.Tab, Key.DirectionRight, Key.DirectionDown -> {
                                        focusVes.requestFocus()
                                        true
                                    }
                                    else -> false
                                }
                            },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Decimal,
                            imeAction = ImeAction.Next,
                        ),
                        keyboardActions = KeyboardActions(onNext = { focusVes.requestFocus() }),
                        shape = RoundedCornerShape(10.dp),
                        colors = currencyFieldColors(),
                    )
                }
                Column(Modifier.weight(1f)) {
                    Text("Monto (${GymlyCurrency.SYMBOL})", color = AdminColors.TextMuted, fontSize = 12.sp)
                    Spacer(Modifier.height(4.dp))
                    OutlinedTextField(
                        value = vesInput,
                        onValueChange = onVesChange,
                        enabled = enabled,
                        modifier = Modifier
                            .fillMaxWidth()
                            .focusRequester(focusVes)
                            .onPreviewKeyEvent { event ->
                                if (event.type != KeyEventType.KeyDown) return@onPreviewKeyEvent false
                                when (event.key) {
                                    Key.DirectionLeft, Key.DirectionUp -> {
                                        focusUsd.requestFocus()
                                        true
                                    }
                                    Key.Tab -> {
                                        if (event.isShiftPressed) {
                                            focusUsd.requestFocus()
                                            true
                                        } else {
                                            false
                                        }
                                    }
                                    else -> false
                                }
                            },
                        singleLine = true,
                        keyboardOptions = KeyboardOptions(
                            keyboardType = KeyboardType.Decimal,
                            imeAction = ImeAction.Done,
                        ),
                        shape = RoundedCornerShape(10.dp),
                        colors = currencyFieldColors(),
                    )
                }
            }
        } else {
            Text("Monto (USD)", color = AdminColors.TextMuted, fontSize = 12.sp)
            Spacer(Modifier.height(4.dp))
            OutlinedTextField(
                value = usdInput,
                onValueChange = onUsdChange,
                enabled = enabled,
                modifier = Modifier
                    .fillMaxWidth()
                    .focusRequester(focusUsd),
                singleLine = true,
                keyboardOptions = KeyboardOptions(
                    keyboardType = KeyboardType.Decimal,
                    imeAction = ImeAction.Done,
                ),
                shape = RoundedCornerShape(10.dp),
                colors = currencyFieldColors(),
            )
            equivBs?.takeIf { it > 0 }?.let { bs ->
                Spacer(Modifier.height(4.dp))
                Text(
                    "Equiv. ref. ${formatMoney(bs.toInt(), GymlyCurrency.CODE)} (no se suma a Total Bs)",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
        }
        if (exchangeRatesOn) {
            Spacer(Modifier.height(8.dp))
            ExchangeRateHint(exchangeQuote)
        }
    }
}

@Composable
fun DualCurrencyTotalHint(
    totalVesCents: Long,
    exchangeQuote: AdminExchangeRateQuote?,
    modifier: Modifier = Modifier,
    totalUsdCents: Long = 0L,
) {
    if (totalVesCents <= 0 && totalUsdCents <= 0) return
    Column(modifier) {
        Text(
            buildList {
                if (totalVesCents > 0) add(formatMoney(totalVesCents.toInt(), GymlyCurrency.CODE))
                if (totalUsdCents > 0) add(formatMoney(totalUsdCents.toInt(), "USD"))
            }.joinToString(" · ").ifBlank { formatMoney(0, GymlyCurrency.CODE) }.let { "Total: $it" },
            fontWeight = FontWeight.Bold,
            fontSize = 16.sp,
            color = AdminColors.Text,
        )
        if (totalUsdCents <= 0 && totalVesCents > 0) {
            val approxUsd = exchangeQuote?.let { convertVesToUsdLocally(totalVesCents, it) }
            if (approxUsd != null && approxUsd > 0) {
                Spacer(Modifier.height(4.dp))
                Text(
                    "≈ ${formatMoney(approxUsd.toInt(), "USD")} · ref. tasa (no suma al cobro)",
                    color = AdminColors.TextMuted,
                    fontSize = 12.sp,
                )
            }
        }
        ExchangeRateHint(exchangeQuote)
    }
}

@Composable
private fun ExchangeRateHint(exchangeQuote: AdminExchangeRateQuote?) {
    when {
        exchangeQuote == null || exchangeQuote.status == AdminExchangeRateStatus.UNAVAILABLE -> {
            Text(
                "Cotización BCV no disponible. Revisa Configuración → Cotización.",
                color = AdminColors.Error,
                fontSize = 12.sp,
            )
        }
        else -> {
            exchangeQuote.effectiveDate?.let { date ->
                Text("Tasa BCV · fecha valor $date", color = AdminColors.TextMuted, fontSize = 12.sp)
            }
        }
    }
}

@Composable
private fun currencyFieldColors() = OutlinedTextFieldDefaults.colors(
    focusedBorderColor = AdminColors.Primary,
    unfocusedBorderColor = AdminColors.Outline,
)

fun centsToAmountInput(cents: Long): String {
    if (cents <= 0) return ""
    val abs = kotlin.math.abs(cents)
    val whole = abs / 100
    val frac = abs % 100
    val body = if (frac == 0L) {
        whole.toString()
    } else {
        "$whole.${frac.toString().padStart(2, '0')}".trimEnd('0').trimEnd('.')
    }
    return if (cents < 0) "-$body" else body
}

/** Cero escrito para un campo de monto (pendientes). centsToAmountInput(0) devuelve vacío a propósito. */
const val AMOUNT_INPUT_ZERO = "0,00"

/**
 * Descuento en Bs para mostrar en vivo en el cobro. Con la tasa encendida y un descuento en $, el backend usa el
 * equivalente en Bs (con la tasa de hoy) y descarta el Bs escrito; aquí se muestra lo mismo. Sin tasa encendida o
 * sin cotización, queda el Bs escrito.
 */
fun discountLiveBsCents(bsCents: Long, usdCents: Long, quote: AdminExchangeRateQuote?, ratesOn: Boolean): Long {
    if (!ratesOn || usdCents <= 0L || quote == null) return bsCents
    // Mismo cálculo que el backend (ExchangeRateService): siempre la tasa del dólar, nunca la del euro.
    val usdRate = quote.usdRate ?: return bsCents
    return (usdCents * usdRate).roundToLong()
}

fun convertVesToUsdLocally(vesCents: Long, quote: AdminExchangeRateQuote): Long? {
    if (vesCents <= 0) return null
    val eurRate = quote.eurRate ?: return null
    val usdRate = quote.usdRate ?: return null
    val vesAmount = vesCents / 100.0
    val usdAmount = if (quote.divisasUsesEurRate) vesAmount / eurRate else vesAmount / usdRate
    return (usdAmount * 100.0).roundToLong()
}

fun resolveForeignCurrencyVesCents(
    usdInput: String,
    vesInput: String,
    defaultAmount: String,
    quote: AdminExchangeRateQuote?,
): Long {
    // Prioriza USD → conversión BCV. No usar el monto Bs del plan (evita sumar 8000 fantasma).
    val usdCents = parseBulkAmountToCents(usdInput)
    if (usdCents > 0 && quote != null) {
        return convertDivisasLocally(usdCents, quote)?.vesCents ?: 0L
    }
    val vesCents = parseBulkAmountToCents(vesInput)
    if (vesCents > 0) return vesCents
    return 0L
}

fun resolveForeignCurrencyUsdCents(
    usdInput: String,
    vesInput: String,
    defaultAmount: String,
    quote: AdminExchangeRateQuote?,
): Long? {
    val usdCents = parseBulkAmountToCents(usdInput)
    if (usdCents > 0) return usdCents
    // Solo si el usuario escribió Bs (modo legacy showVesField) se infiere USD.
    val vesCents = parseBulkAmountToCents(vesInput)
    if (vesCents <= 0 || quote == null) return null
    return convertVesToUsdLocally(vesCents, quote)
}

fun foreignCurrencyLineAmountCents(
    line: PaymentLineDraft,
    defaultAmount: String,
    quote: AdminExchangeRateQuote?,
): Long {
    if (line.method.usesUsdAmount()) return 0L
    return line.amountCents(defaultAmount)
}

fun foreignCurrencyLineUsdCents(
    line: PaymentLineDraft,
    defaultAmount: String,
    quote: AdminExchangeRateQuote?,
): Long? {
    if (!line.method.usesUsdAmount()) return null
    return resolveForeignCurrencyUsdCents(
        usdInput = line.amountInput,
        vesInput = "",
        defaultAmount = defaultAmount,
        quote = quote,
    )
}

fun paymentLineHasPositiveAmount(
    line: PaymentLineDraft,
    defaultAmount: String,
    quote: AdminExchangeRateQuote?,
): Boolean =
    if (line.method.usesUsdAmount()) {
        (foreignCurrencyLineUsdCents(line, defaultAmount, quote) ?: 0L) > 0L
    } else {
        line.amountCents(defaultAmount) > 0L
    }

fun syncDualCurrencyFromVes(vesInput: String, quote: AdminExchangeRateQuote?): Pair<String, String> {
    val vesCents = parseBulkAmountToCents(vesInput)
    if (vesCents <= 0 || quote == null) return "" to vesInput
    val usdCents = convertVesToUsdLocally(vesCents, quote) ?: return "" to vesInput
    return centsToAmountInput(usdCents) to vesInput
}

fun syncDualCurrencyFromUsd(usdInput: String, quote: AdminExchangeRateQuote?): Pair<String, String> {
    val usdCents = parseBulkAmountToCents(usdInput)
    if (usdCents <= 0 || quote == null) return usdInput to ""
    val vesCents = convertDivisasLocally(usdCents, quote)?.vesCents ?: return usdInput to ""
    return usdInput to centsToAmountInput(vesCents)
}

fun applyDualCurrencyUsdChange(
    line: PaymentLineDraft,
    raw: String,
    quote: AdminExchangeRateQuote?,
): PaymentLineDraft {
    // Tras la primera edición manual ya no se recalcula el otro campo.
    if (!line.dualCurrencyLinked) {
        return line.copy(amountInput = raw)
    }
    return line.copy(amountInput = raw, dualCurrencyLinked = false)
}

fun applyDualCurrencyVesChange(
    line: PaymentLineDraft,
    raw: String,
    quote: AdminExchangeRateQuote?,
): PaymentLineDraft {
    if (!line.dualCurrencyLinked) {
        return line.copy(vesAmountInput = raw)
    }
    return line.copy(vesAmountInput = raw, dualCurrencyLinked = false)
}

/** Precio del plan en USD: catálogo del plan o conversión BCV desde Bs. */
fun resolvePlanUsdAmountInput(
    priceUsdCents: Long,
    priceBsCents: Long,
    quote: AdminExchangeRateQuote?,
): String =
    priceUsdCents.takeIf { it > 0 }?.let { centsToAmountInput(it) }
        ?: priceBsCents.takeIf { it > 0 }?.let { bs ->
            quote?.let { syncDualCurrencyFromVes(centsToAmountInput(bs), it).first }
        }.orEmpty()

/** USD por defecto al cobrar: catálogo del plan (proporcional al pendiente) o conversión BCV. */
fun resolveCheckoutUsdDefaultInput(
    dueBsCents: Long,
    catalogUsdCents: Long,
    quote: AdminExchangeRateQuote?,
): String {
    if (dueBsCents <= 0) return ""
    if (catalogUsdCents > 0) return centsToAmountInput(catalogUsdCents)
    return resolvePlanUsdAmountInput(0, dueBsCents, quote)
}

/** USD efectivo para una línea de pago cuando no hay precio en catálogo. */
fun effectiveUsdLineDefault(
    defaultUsdAmount: String,
    defaultBsAmount: String,
    quote: AdminExchangeRateQuote?,
): String {
    if (defaultUsdAmount.isNotBlank()) return defaultUsdAmount
    return resolvePlanUsdAmountInput(0, parseBulkAmountToCents(defaultBsAmount), quote)
}

/** Rellena el campo USD con el precio del plan (limpia montos en Bs mal arrastrados). */
fun seedUsdAmountLine(line: PaymentLineDraft, defaultUsdAmount: String): PaymentLineDraft {
    if (!line.method.usesUsdAmount()) return line
    return line.copy(
        amountInput = defaultUsdAmount.ifBlank { line.amountInput },
        vesAmountInput = "",
        dualCurrencyLinked = false,
    )
}

fun shouldSeedUsdAmount(
    line: PaymentLineDraft,
    defaultBsAmount: String,
    defaultUsdAmount: String,
): Boolean {
    if (!line.method.usesUsdAmount() || defaultUsdAmount.isBlank()) return false
    if (line.amountInput.isBlank()) return true
    val inputCents = parseBulkAmountToCents(line.amountInput)
    val bsDefaultCents = parseBulkAmountToCents(defaultBsAmount)
    return bsDefaultCents > 0 && inputCents == bsDefaultCents
}

/** @deprecated Use [seedUsdAmountLine] */
fun seedDualCurrencyLine(
    line: PaymentLineDraft,
    defaultUsdAmount: String,
    quote: AdminExchangeRateQuote?,
): PaymentLineDraft = seedUsdAmountLine(line, defaultUsdAmount)
